import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Pagination,
    Select,
    Stack,
    Group,
    Box,
    Flex,
    ScrollArea,
    Tabs,
    Tooltip,
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import {
    IconRefresh,
    IconDownload,
    IconSearch,
    IconCirclePlus,
    IconCircleCheck,
    IconAlertTriangle,
    IconArrowsShuffle,
    IconClockHour2,
    IconLayoutGridAdd,
    IconHourglassEmpty,
} from '@tabler/icons-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { DataTable, DataTableColumn, DataTableProps } from 'mantine-datatable';

import {
    DashboardHeader,
    DebouncedInput,
    ReportCard,
    PageZoomControls,
    When,
} from '../../../components/common';
import { PageZoomHost } from '../../../context/PageZoomContext';
import DateRangePickerWithPresets from '../../../components/common/DateRangePickerWithPresets';
import { commonTableStyles } from '../../../constants/tableStyles';
import { useAuth } from '../../../context/AuthProvider';
import RequestSurveyModal from '../../../components/modals/RequestSurveyModal';
import { CancelSurveyConfirmModal } from '../../../components/modals/CancelSurveyConfirmModal';
import { SurveyHistoryDrawer } from '../../../components/survey/SurveyHistoryDrawer';
import { getActiveSurveysColumns, getSurveyHistoryColumns } from '../columns';
import { ACTIVE_SURVEY_STATUSES, HISTORY_SURVEY_STATUSES } from '../constants';
import { SurveyRow } from '../types';
import { checkUserRole, isUserAdmin } from '../../../utility/userRoles';
import { UserRole } from '../../../types/users';
import { usePriceSurveysData } from '../hooks/usePriceSurveysData';
import { useExportSurveys } from '../../../hooks/useExportSurveys';
import { GET_ACCESSIBLE_ADVERTISING_ZONES } from '../../../apollo/queries/advertisingZone';
import { useGetAccessibleSurveyStores } from '../../../services/survey';
import {
    buildSurveyScopeFilterOptions,
    mapScopeFilterLabelsToIds,
    mapSurveyTypeFilterLabels,
    SurveyTableColumnFilters,
} from '../surveyTableFilters';

function PriceSurveysHome() {
    const { authUser } = useAuth();
    const isStoreManagerUser = checkUserRole(authUser, [
        UserRole.STORE_MANAGER,
    ]);
    const isDivisionalManagerUser = checkUserRole(authUser, [
        UserRole.DIVISIONAL_MANAGER,
    ]);
    const canCancelSurveys = isDivisionalManagerUser || isUserAdmin(authUser);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<string | null>('active-surveys');
    const [surveyHistoryDrawer, setSurveyHistoryDrawer] = useState<{
        opened: boolean;
        surveyId: string | null;
    }>({ opened: false, surveyId: null });
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchValue, 500);
    const [scopeFilter, setScopeFilter] = useState<string[] | null>(null);
    const [surveyTypeFilter, setSurveyTypeFilter] = useState<string[] | null>(
        null,
    );
    const [labelSearch, setLabelSearch] = useState('');
    const [debouncedLabelSearch] = useDebouncedValue(labelSearch, 500);
    const [statusFilter, setStatusFilter] = useState<string[] | null>(null);
    const [dateRange, setDateRange] = useState<
        readonly [Date | null, Date | null]
    >([
        dayjs().subtract(30, 'days').startOf('day').toDate(),
        dayjs().endOf('day').toDate(),
    ]);
    const [requestSurveyModalOpened, requestSurveyModalHandlers] =
        useDisclosure(false);
    const [cancelSurveyId, setCancelSurveyId] = useState<string | null>(null);
    const [
        cancelSurveyModalOpened,
        { open: openCancelSurveyModal, close: closeCancelSurveyModal },
    ] = useDisclosure(false);
    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    const { data: advertisingZonesData, loading: loadingZones } = useQuery(
        GET_ACCESSIBLE_ADVERTISING_ZONES,
        { skip: isStoreManagerUser },
    );
    const advertisingZones =
        advertisingZonesData?.getAccessibleAdvertisingZones || [];

    const { data: surveyStoresData, loading: loadingStores } =
        useGetAccessibleSurveyStores(isStoreManagerUser);
    const surveyStores = surveyStoresData?.getAccessibleSurveyStores || [];

    const statusesToFilter = useMemo(() => {
        if (statusFilter && statusFilter.length > 0) {
            return statusFilter;
        }
        if (activeTab === 'active-surveys') {
            return [...ACTIVE_SURVEY_STATUSES];
        }
        if (activeTab === 'survey-history') {
            return [...HISTORY_SURVEY_STATUSES];
        }
        return undefined;
    }, [activeTab, statusFilter]);

    const scopeFilterIds = useMemo(() => {
        if (isStoreManagerUser) {
            return {};
        }
        return mapScopeFilterLabelsToIds(
            scopeFilter,
            advertisingZones,
            surveyStores,
        );
    }, [isStoreManagerUser, scopeFilter, advertisingZones, surveyStores]);
    const selectedSurveyTypes = useMemo(
        () => mapSurveyTypeFilterLabels(surveyTypeFilter),
        [surveyTypeFilter],
    );

    // Backend handles: pagination, status filtering, and search
    const { exporting, exportSurveys } = useExportSurveys();
    const {
        tableData,
        pagination: backendPagination,
        counts,
        isInitialLoading,
        isTableLoading,
        refetch,
    } = usePriceSurveysData({
        limit: paginationState.pageSize,
        offset: paginationState.pageIndex * paginationState.pageSize,
        statuses: statusesToFilter,
        search: debouncedSearch || undefined,
        advertisingZoneIds: scopeFilterIds.advertisingZoneIds,
        storeIds: scopeFilterIds.storeIds,
        surveyTypes: selectedSurveyTypes,
        label: debouncedLabelSearch || undefined,
        createdAtStart: dateRange[0],
        createdAtEnd: dateRange[1],
    });

    const totalRecords = backendPagination.total;

    const reportCardData = [
        {
            value: counts?.active ?? 0,
            label: 'Surveys',
            icon: <IconLayoutGridAdd size={28} />,
            iconBgClass: 'bg-blue-100',
            iconColorClass: 'text-blue-600',
        },
        {
            value: counts?.notStarted ?? 0,
            label: 'Pending',
            icon: <IconHourglassEmpty size={28} />,
            iconBgClass: 'bg-amber-100',
            iconColorClass: 'text-amber-700',
        },
        {
            value: counts?.inProgress ?? 0,
            label: 'In Progress',
            icon: <IconClockHour2 size={28} />,
            iconBgClass: 'bg-report-orange-light',
            iconColorClass: 'text-report-orange',
        },
        {
            value: counts?.completed ?? 0,
            label: 'Completed',
            icon: <IconCircleCheck size={28} />,
            iconBgClass: 'bg-emerald-100',
            iconColorClass: 'text-emerald-500',
        },
        {
            value: counts?.overdue ?? 0,
            label: 'Overdue',
            icon: <IconAlertTriangle size={28} />,
            iconBgClass: 'bg-red-100',
            iconColorClass: 'text-red-500',
        },
        {
            value: counts?.priceChangesGenerated ?? 0,
            label: 'Price Changes Generated',
            icon: <IconArrowsShuffle size={28} />,
            iconBgClass: 'bg-green-100',
            iconColorClass: 'text-green-500',
        },
    ];

    const statusFilterOptions = useMemo(() => {
        if (activeTab === 'active-surveys') {
            return [...ACTIVE_SURVEY_STATUSES];
        }
        if (activeTab === 'survey-history') {
            return [...HISTORY_SURVEY_STATUSES];
        }
        return [];
    }, [activeTab]);

    const scopeFilterOptions = useMemo(
        () => buildSurveyScopeFilterOptions(advertisingZones, surveyStores),
        [advertisingZones, surveyStores],
    );

    const handleScopeFilterChange = useCallback((value: string[] | null) => {
        setScopeFilter(value);
        setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    }, []);

    const handleSurveyTypeFilterChange = useCallback(
        (value: string[] | null) => {
            setSurveyTypeFilter(value);
            setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
        },
        [],
    );

    const handleStatusFilterChange = useCallback((value: string[] | null) => {
        setStatusFilter(value);
        setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    }, []);

    const columnFilters = useMemo(
        (): SurveyTableColumnFilters => ({
            scopeFilter,
            onScopeFilterChange: handleScopeFilterChange,
            scopeFilterOptions,
            scopeFilterLoading: loadingZones || loadingStores,
            showScopeFilter: !isStoreManagerUser,
            surveyTypeFilter,
            onSurveyTypeFilterChange: handleSurveyTypeFilterChange,
            statusFilter,
            onStatusFilterChange: handleStatusFilterChange,
            statusFilterOptions,
        }),
        [
            scopeFilter,
            handleScopeFilterChange,
            scopeFilterOptions,
            loadingZones,
            loadingStores,
            isStoreManagerUser,
            surveyTypeFilter,
            handleSurveyTypeFilterChange,
            statusFilter,
            handleStatusFilterChange,
            statusFilterOptions,
        ],
    );

    const openSurveyHistoryDrawer = useCallback((surveyId: string) => {
        setSurveyHistoryDrawer({ opened: true, surveyId });
    }, []);

    const closeSurveyHistoryDrawer = useCallback(() => {
        setSurveyHistoryDrawer({ opened: false, surveyId: null });
    }, []);

    const handleCancelSurvey = useCallback(
        (surveyId: string) => {
            setCancelSurveyId(surveyId);
            openCancelSurveyModal();
        },
        [openCancelSurveyModal],
    );

    const handleCancelSurveyModalClose = useCallback(() => {
        closeCancelSurveyModal();
        setCancelSurveyId(null);
    }, [closeCancelSurveyModal]);

    const cancelSurveyHandler = canCancelSurveys
        ? handleCancelSurvey
        : undefined;

    const activeSurveysColumns = useMemo(
        () =>
            getActiveSurveysColumns(
                isStoreManagerUser,
                isDivisionalManagerUser,
                columnFilters,
                cancelSurveyHandler,
            ),
        [
            isStoreManagerUser,
            isDivisionalManagerUser,
            columnFilters,
            cancelSurveyHandler,
        ],
    );

    const surveyHistoryColumns = useMemo(
        () =>
            getSurveyHistoryColumns(
                isStoreManagerUser,
                isDivisionalManagerUser,
                columnFilters,
                openSurveyHistoryDrawer,
            ),
        [
            isStoreManagerUser,
            isDivisionalManagerUser,
            columnFilters,
            openSurveyHistoryDrawer,
        ],
    );

    const handleRowClick = useCallback(
        (row: SurveyRow) => {
            navigate(`/price-surveys/survey/${row.id}`);
        },
        [navigate],
    );

    useEffect(() => {
        if (isStoreManagerUser) {
            setScopeFilter(null);
        }
    }, [isStoreManagerUser]);

    useEffect(() => {
        setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    }, [
        debouncedSearch,
        debouncedLabelSearch,
        scopeFilter,
        surveyTypeFilter,
        dateRange,
    ]);

    const handleTabChange = (tab: string | null) => {
        setActiveTab(tab);
        setStatusFilter(null);
        setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    };

    const handlePageSizeChange = (value: string | null) => {
        if (value) {
            setPaginationState({
                pageIndex: 0,
                pageSize: parseInt(value, 10),
            });
        }
    };

    const handleRefresh = useCallback(() => {
        setSearchValue('');
        setScopeFilter(null);
        setSurveyTypeFilter(null);
        setLabelSearch('');
        setStatusFilter(null);
        setDateRange([
            dayjs().subtract(30, 'days').startOf('day').toDate(),
            dayjs().endOf('day').toDate(),
        ]);
        setPaginationState({ pageIndex: 0, pageSize: 20 });
        refetch().catch(() => {});
    }, [refetch]);

    const handleExportSurveys = useCallback(() => {
        exportSurveys(null, {
            statuses: statusesToFilter,
            search: debouncedSearch || undefined,
            advertisingZoneIds: isStoreManagerUser
                ? undefined
                : scopeFilterIds.advertisingZoneIds,
            storeIds: isStoreManagerUser ? undefined : scopeFilterIds.storeIds,
            surveyTypes: selectedSurveyTypes,
            createdAtStart: dateRange[0]
                ? dayjs(dateRange[0]).toISOString()
                : undefined,
            createdAtEnd: dateRange[1]
                ? dayjs(dateRange[1]).toISOString()
                : undefined,
        }).catch(() => {});
    }, [
        exportSurveys,
        statusesToFilter,
        debouncedSearch,
        isStoreManagerUser,
        scopeFilterIds,
        selectedSurveyTypes,
        dateRange,
    ]);

    const { exportButtonText, exportButtonClassName, exportTooltipLabel } =
        useMemo(() => {
            const text = exporting ? 'Exporting...' : 'Export';
            const className = exporting
                ? 'text-gray-400 border-gray-300 cursor-not-allowed'
                : 'text-primary border-primary hover:bg-primary/10';
            const tooltipLabel = exporting
                ? 'Export in progress'
                : 'Click to export all surveys matching current filters';
            return {
                exportButtonText: text,
                exportButtonClassName: className,
                exportTooltipLabel: tooltipLabel,
            };
        }, [exporting]);

    const getDataTableProps = (
        columns: DataTableColumn<SurveyRow>[],
        onRowClick?: (row: SurveyRow) => void,
    ): DataTableProps<SurveyRow> => ({
        withTableBorder: true,
        striped: true,
        verticalSpacing: 'xs',
        fz: 'sm',
        noRecordsText: 'No survey data available',
        withColumnBorders: false,
        highlightOnHover: true,
        records: tableData,
        columns,
        fetching: isTableLoading,
        idAccessor: 'id',
        classNames: {
            table: 'selection-column-fix',
        },
        styles: commonTableStyles,
        ...(onRowClick && {
            onRowClick: ({ record }) => onRowClick(record),
        }),
    });

    const showRequestSurveyButton = isDivisionalManagerUser;
    const showReportCards = isDivisionalManagerUser;

    return (
        <div className="relative">
            <DashboardHeader
                title="Price Surveys - Management"
                hasBreadcrumb={false}
                hasDate
                isLoading={isInitialLoading}
                rightComponent={
                    <Group gap="xs" wrap="nowrap" align="center">
                        <DateRangePickerWithPresets
                            value={dateRange as [Date | null, Date | null]}
                            onChange={setDateRange}
                            className="shrink-0"
                        />
                        <When condition={showRequestSurveyButton}>
                            <Box className="flex-shrink-0">
                                <Button
                                    fullWidth={false}
                                    leftSection={<IconCirclePlus size={20} />}
                                    onClick={requestSurveyModalHandlers.open}
                                    size="md"
                                >
                                    Request Survey
                                </Button>
                            </Box>
                        </When>
                    </Group>
                }
            />

            <When condition={showReportCards}>
                <Box className="px-11 mt-6">
                    <ScrollArea type="never" offsetScrollbars={false}>
                        <Flex gap="lg" pb="xs">
                            {reportCardData.map(card => (
                                <ReportCard
                                    key={card.label}
                                    value={card.value}
                                    label={card.label}
                                    icon={card.icon}
                                    iconBgClass={card.iconBgClass}
                                    iconColorClass={card.iconColorClass}
                                />
                            ))}
                        </Flex>
                    </ScrollArea>
                </Box>
            </When>

            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className="px-11"
            >
                <Tabs.List>
                    <Tabs.Tab value="active-surveys">Active Surveys</Tabs.Tab>
                    <Tabs.Tab value="survey-history">Survey History</Tabs.Tab>
                </Tabs.List>

                <Box className="w-full mt-5">
                    <div
                        className={clsx(
                            'w-full bg-white shadow-md rounded-xl rounded-t-none flex flex-col',
                            isDivisionalManagerUser
                                ? 'h-[calc(100vh-400px)]'
                                : 'h-[calc(100vh-275px)]',
                        )}
                    >
                        <Stack p="md" pb="sm" gap={0} flex={0}>
                            <Flex
                                w="100%"
                                align="center"
                                direction="row"
                                wrap="nowrap"
                                gap="md"
                                justify="space-between"
                            >
                                <DebouncedInput
                                    leftSection={<IconSearch />}
                                    placeholder="Search"
                                    value={searchValue}
                                    onChange={e =>
                                        setSearchValue(e.target.value)
                                    }
                                    className="w-64 shrink-0"
                                />
                                <Group
                                    gap="md"
                                    wrap="nowrap"
                                    align="center"
                                    className="shrink-0"
                                >
                                    <DebouncedInput
                                        placeholder="Search survey name"
                                        value={labelSearch}
                                        onChange={e =>
                                            setLabelSearch(e.target.value)
                                        }
                                        className="w-52 shrink-0"
                                    />
                                    <Tooltip
                                        label={exportTooltipLabel}
                                        openDelay={100}
                                    >
                                        <div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                leftSection={
                                                    <IconDownload size={16} />
                                                }
                                                onClick={handleExportSurveys}
                                                disabled={exporting}
                                                className={
                                                    exportButtonClassName
                                                }
                                            >
                                                {exportButtonText}
                                            </Button>
                                        </div>
                                    </Tooltip>
                                    <Button
                                        variant="subtle"
                                        className="text-primary rounded-md p-1 w-min h-min"
                                        onClick={handleRefresh}
                                    >
                                        <IconRefresh />
                                    </Button>
                                </Group>
                            </Flex>
                        </Stack>
                        <Stack flex={1} px="md" className="min-h-0" gap={0}>
                            <PageZoomHost>
                                <Box className="h-full min-h-0 overflow-auto">
                                    <Tabs.Panel value="active-surveys">
                                        <DataTable
                                            {...getDataTableProps(
                                                activeSurveysColumns,
                                                handleRowClick,
                                            )}
                                        />
                                    </Tabs.Panel>
                                    <Tabs.Panel value="survey-history">
                                        <DataTable
                                            {...getDataTableProps(
                                                surveyHistoryColumns,
                                                handleRowClick,
                                            )}
                                        />
                                    </Tabs.Panel>
                                </Box>
                            </PageZoomHost>
                            <Group
                                gap="sm"
                                w="100%"
                                justify="flex-end"
                                wrap="nowrap"
                                py="xs"
                                className="shrink-0 border-t border-gray-100"
                            >
                                <PageZoomControls />
                                <Select
                                    data={['10', '20', '50', '100']}
                                    value={paginationState.pageSize.toString()}
                                    onChange={handlePageSizeChange}
                                    size="xs"
                                    className="w-20 shrink-0"
                                />
                                <Pagination
                                    value={paginationState.pageIndex + 1}
                                    total={Math.ceil(
                                        totalRecords / paginationState.pageSize,
                                    )}
                                    onChange={p =>
                                        setPaginationState(prev => ({
                                            ...prev,
                                            pageIndex: p - 1,
                                        }))
                                    }
                                    size="sm"
                                />
                                <span className="text-xs text-gray-500 ml-2">
                                    {totalRecords === 0
                                        ? '0 of 0'
                                        : `${
                                              paginationState.pageSize *
                                                  paginationState.pageIndex +
                                              1
                                          }-${Math.min(
                                              paginationState.pageSize *
                                                  (paginationState.pageIndex +
                                                      1),
                                              totalRecords,
                                          )} of ${totalRecords}`}
                                </span>
                            </Group>
                        </Stack>
                    </div>
                </Box>
            </Tabs>

            <RequestSurveyModal
                opened={requestSurveyModalOpened}
                close={requestSurveyModalHandlers.close}
            />

            <SurveyHistoryDrawer
                surveyId={surveyHistoryDrawer.surveyId ?? ''}
                opened={surveyHistoryDrawer.opened}
                onClose={closeSurveyHistoryDrawer}
            />

            <CancelSurveyConfirmModal
                opened={cancelSurveyModalOpened}
                onClose={handleCancelSurveyModalClose}
                surveyId={cancelSurveyId}
            />
        </div>
    );
}

export default PriceSurveysHome;
